import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "./api";
import { Pencil, Trash2, Save, X, ImagePlus } from "lucide-react";

function PartsList() {
  const [parts, setParts] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [partImages, setPartImages] = useState({});
  const [uploadingPartId, setUploadingPartId] = useState(null);

  const [editForm, setEditForm] = useState({
    part_id: "",
    line: "",
    machine: "",
    equipment: "",
    part_name: "",
    quantity: "",
    location: "",
  });

  const API_BASE = (api?.defaults?.baseURL || "").replace(/\/$/, "") || "";

  const imgViewUrl = (imageId) =>
    API_BASE
      ? `${API_BASE}/api/parts/images/${imageId}/view`
      : `/api/parts/images/${imageId}/view`;

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = () => {
    api
      .get("/api/parts")
      .then(async (res) => {
        const arr = res.data || [];
        setParts(arr);
        await fetchImagesForParts(arr);
      })
      .catch((err) => {
        console.error("Error fetching parts", err);
        alert("❌ Failed to load parts");
      });
  };

  const fetchImagesForParts = async (partsArr) => {
    try {
      const map = {};
      await Promise.all(
        partsArr.map(async (p) => {
          try {
            const r = await api.get(`/api/parts/${p.id}/images`);
            map[p.id] = r.data || [];
          } catch {
            map[p.id] = [];
          }
        })
      );
      setPartImages(map);
    } catch (e) {
      console.error("Error fetching part images", e);
    }
  };

  const refreshImagesForPart = async (partId) => {
    try {
      const r = await api.get(`/api/parts/${partId}/images`);
      setPartImages((prev) => ({ ...prev, [partId]: r.data || [] }));
    } catch (e) {
      console.error("Error refreshing images", e);
    }
  };

  const uploadPartImages = async (partId, files) => {
    if (!files || files.length === 0) return;

    setUploadingPartId(partId);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("image", file);

        await api.post(`/api/parts/${partId}/images/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await refreshImagesForPart(partId);
    } catch (e) {
      console.error("Upload failed", e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e.message ||
        "Unknown error";
      alert(`❌ Failed to upload image(s): ${msg}`);
    } finally {
      setUploadingPartId(null);
    }
  };

  const deletePartImage = async (partId, imageId) => {
    if (!window.confirm("Delete this picture?")) return;

    try {
      await api.delete(`/api/parts/images/${imageId}`);
      await refreshImagesForPart(partId);
    } catch (e) {
      console.error("Delete image failed", e);
      alert("❌ Failed to delete image");
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      part_id: p.part_id || "",
      line: p.line || "",
      machine: p.machine || "",
      equipment: p.equipment || "",
      part_name: p.part_name || "",
      quantity: p.quantity ?? "",
      location: p.location || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      part_id: "",
      line: "",
      machine: "",
      equipment: "",
      part_name: "",
      quantity: "",
      location: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: name === "quantity" ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/parts/${id}`, {
        ...editForm,
        quantity: Number(editForm.quantity || 0),
      });
      cancelEdit();
      fetchParts();
    } catch (err) {
      console.error("Error updating part", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to update part: ${msg}`);
    }
  };

  const deletePart = async (id) => {
    if (!window.confirm("Delete this part?")) return;

    try {
      await api.delete(`/api/parts/${id}`);
      fetchParts();
    } catch (err) {
      console.error("Error deleting part", err);
      alert("❌ Failed to delete part");
    }
  };

  return (
    <div className="main-content">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h1>🛠️ Parts Inventory</h1>
        <Link to="/add-part" className="add-button">
          + Add Part
        </Link>
      </div>
    
      {editingId && (
  <div
    style={{
      marginBottom: 12,
      padding: 12,
      borderRadius: 12,
      background: "#ecfdf5",
      border: "1px solid #22c55e",
      display: "flex",
      gap: 10,
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <strong style={{ color: "#166534" }}>
      Editing part — click Save Changes when done
    </strong>

    <div style={{ display: "flex", gap: 10 }}>
      <button
        type="button"
        onClick={() => saveEdit(editingId)}
        style={{
          background: "#16a34a",
          color: "white",
          border: "none",
          borderRadius: 8,
          padding: "8px 14px",
          cursor: "pointer",
          fontWeight: 700,
        }}
      >
        💾 Save Changes
      </button>

      <button
        type="button"
        onClick={cancelEdit}
        style={{
          background: "#6b7280",
          color: "white",
          border: "none",
          borderRadius: 8,
          padding: "8px 14px",
          cursor: "pointer",
          fontWeight: 700,
        }}
      >
        Cancel
      </button>
    </div>
  </div>
)}

      <div className="table-wrapper">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Part ID</th>
                <th>Line</th>
                <th>Machine</th>
                <th>Equipment</th>
                <th>Part Name</th>
                <th>Quantity</th>
                <th>Location</th>
                <th>Pictures</th>
                <th
                  style={{
                    width: 170,
                    position: "sticky",
                    right: 0,
                    background: "#ffffff",
                    zIndex: 10,
                    boxShadow: "-2px 0 6px rgba(0,0,0,0.08)",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {parts.map((p) => {
                const imgs = partImages[p.id] || [];
                const isEditing = editingId === p.id;

                return (
                  <tr key={p.id}>
                    <td>{p.id}</td>

                    <td>
                      {isEditing ? (
                        <input
                          name="part_id"
                          value={editForm.part_id}
                          onChange={handleEditChange}
                        />
                      ) : (
                        p.part_id
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="line"
                          value={editForm.line}
                          onChange={handleEditChange}
                        />
                      ) : (
                        p.line
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="machine"
                          value={editForm.machine}
                          onChange={handleEditChange}
                        />
                      ) : (
                        p.machine
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="equipment"
                          value={editForm.equipment}
                          onChange={handleEditChange}
                        />
                      ) : (
                        p.equipment
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="part_name"
                          value={editForm.part_name}
                          onChange={handleEditChange}
                        />
                      ) : (
                        p.part_name
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="quantity"
                          type="number"
                          value={editForm.quantity}
                          onChange={handleEditChange}
                          min="0"
                        />
                      ) : (
                        p.quantity
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          name="location"
                          value={editForm.location}
                          onChange={handleEditChange}
                        />
                      ) : (
                        p.location
                      )}
                    </td>

                    <td style={{ minWidth: 280 }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        {imgs.length === 0 ? (
                          <span style={{ fontSize: 12, color: "#9ca3af" }}>
                            No pictures
                          </span>
                        ) : (
                          imgs.slice(0, 6).map((img) => (
                            <div key={img.id} style={{ position: "relative" }}>
                              <a
                                href={imgViewUrl(img.id)}
                                target="_blank"
                                rel="noreferrer"
                                title={img.original_name}
                              >
                                <img
                                  src={imgViewUrl(img.id)}
                                  alt={img.original_name}
                                  style={{
                                    width: 54,
                                    height: 54,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    border:
                                      "1px solid rgba(148, 163, 184, 0.35)",
                                  }}
                                />
                              </a>

                              <button
                                type="button"
                                className="icon-btn danger"
                                onClick={() => deletePartImage(p.id, img.id)}
                                title="Delete picture"
                                style={{
                                  position: "absolute",
                                  top: -10,
                                  right: -10,
                                  width: 26,
                                  height: 26,
                                  padding: 0,
                                  borderRadius: 999,
                                  background: "rgba(255,255,255,0.95)",
                                  border: "1px solid rgba(148,163,184,0.35)",
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))
                        )}

                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            color: "#9ca3af",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              uploadPartImages(p.id, files);
                              e.target.value = "";
                            }}
                          />

                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 10px",
                              borderRadius: 999,
                              border: "1px solid rgba(148, 163, 184, 0.35)",
                              background: "rgba(15, 23, 42, 0.06)",
                              color: "#475569",
                              fontWeight: 600,
                            }}
                          >
                            <ImagePlus size={14} />
                            {uploadingPartId === p.id
                              ? "Uploading..."
                              : "Add pics"}
                          </span>
                        </label>

                        {imgs.length > 6 && (
                          <span style={{ fontSize: 12, color: "#9ca3af" }}>
                            +{imgs.length - 6} more
                          </span>
                        )}
                      </div>
                    </td>

                    <td
                      style={{
                        position: "sticky",
                        right: 0,
                        background: "#ffffff",
                        zIndex: 5,
                        minWidth: 170,
                        boxShadow: "-2px 0 6px rgba(0,0,0,0.08)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={() => saveEdit(p.id)}
                              title="Save"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                background: "#16a34a",
                                color: "white",
                                border: "none",
                                borderRadius: 8,
                                padding: "6px 10px",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              <Save size={16} />
                              Save
                            </button>

                            <button
                              type="button"
                              onClick={cancelEdit}
                              title="Cancel"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                background: "#6b7280",
                                color: "white",
                                border: "none",
                                borderRadius: 8,
                                padding: "6px 10px",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              <X size={16} />
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => startEdit(p)}
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>

                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={() => deletePart(p.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {parts.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ padding: 14, color: "#9ca3af" }}>
                    No parts yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
        Tip: Click any thumbnail to open the full image in a new tab.
      </p>
    </div>
  );
}

export default PartsList;